import { useMemo } from 'react';
import { DEFAULT_TLDS, MAX_DOMAINS_PER_REQUEST } from '@domaintriage/shared';
import { normalizeDomain, validateDomain } from '../lib/validate';

interface InputScreenProps {
  inputText: string;
  tlds: string[];
  mode: 'A' | 'B';
  onInputChange: (text: string) => void;
  onTldsChange: (tlds: string[]) => void;
  onModeChange: (mode: 'A' | 'B') => void;
  onSubmit: () => void;
  error?: string | null;
}

interface ParsedEntry {
  domain: string;
  line: number;
}

/**
 * Parse the textarea input into domain entries.
 * Supports newline-separated and comma-separated inputs.
 * In Mode B, each base name is expanded with the selected TLDs.
 */
function parseDomains(
  input: string,
  mode: 'A' | 'B',
  tlds: string[],
): { entries: ParsedEntry[]; totalParsed: number } {
  if (!input.trim()) {
    return { entries: [], totalParsed: 0 };
  }

  const lines = input.split(/\n/);
  const allEntries: ParsedEntry[] = [];

  for (let i = 0; i < lines.length; i++) {
    const lineNum = i + 1;
    const parts = lines[i].split(/,/);
    for (const part of parts) {
      const normalized = normalizeDomain(part);
      if (!normalized) continue;

      if (mode === 'B') {
        for (const tld of tlds) {
          const domain = normalized.includes('.')
            ? normalized
            : `${normalized}.${tld}`;
          allEntries.push({ domain, line: lineNum });
        }
      } else {
        allEntries.push({ domain: normalized, line: lineNum });
      }
    }
  }

  // Deduplicate
  const seen = new Set<string>();
  const unique: ParsedEntry[] = [];
  for (const entry of allEntries) {
    if (!seen.has(entry.domain)) {
      seen.add(entry.domain);
      unique.push(entry);
    }
  }

  return {
    entries: unique.slice(0, MAX_DOMAINS_PER_REQUEST),
    totalParsed: unique.length,
  };
}

export function InputScreen({
  inputText,
  tlds,
  mode,
  onInputChange,
  onTldsChange,
  onModeChange,
  onSubmit,
  error,
}: InputScreenProps) {
  const parsed = useMemo(() => parseDomains(inputText, mode, tlds), [inputText, mode, tlds]);

  const validationErrors = useMemo(() => {
    const errors: Array<{ line: number; domain: string; error: string }> = [];
    for (const entry of parsed.entries) {
      const result = validateDomain(entry.domain);
      if (!result.valid && result.error) {
        errors.push({ line: entry.line, domain: entry.domain, error: result.error });
      }
    }
    return errors;
  }, [parsed.entries]);

  const validCount = parsed.entries.length - validationErrors.length;
  const totalParsed = parsed.totalParsed;
  const isTruncated = totalParsed > MAX_DOMAINS_PER_REQUEST;
  const hasValidDomains = validCount > 0;

  function handleTldToggle(tld: string) {
    if (tlds.includes(tld)) {
      if (tlds.length <= 1) return;
      onTldsChange(tlds.filter((t) => t !== tld));
    } else {
      onTldsChange([...tlds, tld]);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (hasValidDomains) {
      onSubmit();
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (hasValidDomains) {
        onSubmit();
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-2xl">
      {/* Hero */}
      <div className="animate-fade-in-up stagger-1 mb-10 text-center">
        <h2 className="text-gradient pb-1 text-4xl font-bold tracking-tight sm:text-5xl">
          Domain Availability, Triaged.
        </h2>
        <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
          Paste up to {MAX_DOMAINS_PER_REQUEST} domains. Get instant DNS &amp; RDAP evidence.
        </p>
      </div>

      {/* Form card */}
      <div className="animate-fade-in-up stagger-2 space-y-6 rounded-2xl border border-gray-200/80 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8 dark:border-gray-800/80 dark:bg-gray-900/50">
        {/* Mode Toggle - Segmented Control */}
        <fieldset>
          <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Input Mode
          </legend>
          <div className="relative flex rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
            {/* Sliding indicator */}
            <div
              className="absolute inset-y-1 w-[calc(50%-4px)] rounded-lg bg-white shadow-sm transition-all duration-300 ease-out dark:bg-gray-700"
              style={{ left: mode === 'A' ? '4px' : '50%' }}
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={() => onModeChange('A')}
              aria-pressed={mode === 'A'}
              className={`relative z-10 flex-1 rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-colors ${
                mode === 'A'
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              Full Domains
              <span className="mt-0.5 block text-xs font-normal opacity-60">As entered</span>
            </button>
            <button
              type="button"
              onClick={() => onModeChange('B')}
              aria-pressed={mode === 'B'}
              className={`relative z-10 flex-1 rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-colors ${
                mode === 'B'
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              Base Names
              <span className="mt-0.5 block text-xs font-normal opacity-60">
                &times; TLDs
              </span>
            </button>
          </div>
        </fieldset>

        {/* TLD Pills (Mode B only) */}
        {mode === 'B' && (
          <fieldset>
            <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              TLDs to check
            </legend>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_TLDS.map((tld) => {
                const active = tlds.includes(tld);
                return (
                  <button
                    key={tld}
                    type="button"
                    onClick={() => handleTldToggle(tld)}
                    aria-pressed={active}
                    className={`rounded-full border px-4 py-1.5 font-mono text-sm font-medium transition-all ${
                      active
                        ? 'border-sky-500/40 bg-sky-50 text-sky-700 dark:border-sky-400/30 dark:bg-sky-950/50 dark:text-sky-300'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:border-gray-700 dark:text-gray-400 dark:hover:border-gray-600'
                    }`}
                  >
                    .{tld}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}

        {/* Textarea */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label
              htmlFor="domain-input"
              className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
            >
              {mode === 'A' ? 'Domains' : 'Base Names'}
            </label>
            <span
              className={`font-mono text-sm font-semibold tabular-nums ${
                validCount === 0
                  ? 'text-gray-400 dark:text-gray-600'
                  : validCount >= MAX_DOMAINS_PER_REQUEST
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {validCount}/{MAX_DOMAINS_PER_REQUEST}
            </span>
          </div>

          <textarea
            id="domain-input"
            value={inputText}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              mode === 'A'
                ? 'example.com\nmyproject.net\ncoolstartup.org'
                : 'myproject\ncoolstartup\nbrandname'
            }
            rows={8}
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
            className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3.5 font-mono text-sm leading-relaxed text-gray-900 transition-all placeholder:text-gray-400 focus:border-sky-500/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-100 dark:placeholder:text-gray-600 dark:focus:border-sky-400/50 dark:focus:bg-gray-800"
          />

          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            One per line, or comma-separated. Press{' '}
            <kbd className="rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] dark:border-gray-700 dark:bg-gray-800">
              Ctrl+Enter
            </kbd>{' '}
            to submit.
          </p>
        </div>

        {/* Truncation Notice */}
        {isTruncated && (
          <div
            role="status"
            className="rounded-xl border border-amber-200/60 bg-amber-50/80 px-4 py-3 text-sm text-amber-800 dark:border-amber-700/30 dark:bg-amber-950/50 dark:text-amber-200"
          >
            Only the first {MAX_DOMAINS_PER_REQUEST} domains will be checked.{' '}
            {totalParsed - MAX_DOMAINS_PER_REQUEST} domain
            {totalParsed - MAX_DOMAINS_PER_REQUEST === 1 ? ' was' : 's were'} truncated.
          </div>
        )}

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="space-y-1.5">
            {validationErrors.map(({ line, domain, error: errMsg }) => (
              <p
                key={`${line}-${domain}`}
                className="text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                <span className="font-mono font-medium">{domain}</span>: {errMsg}
              </p>
            ))}
          </div>
        )}

        {/* API / submission error */}
        {error && (
          <div
            role="alert"
            className="rounded-xl border border-red-200/60 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-700/30 dark:bg-red-950/50 dark:text-red-300"
          >
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={!hasValidDomains}
          className="w-full rounded-xl bg-gradient-to-r from-sky-500 to-violet-500 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-sky-500/25 transition-all hover:from-sky-600 hover:to-violet-600 hover:shadow-xl hover:shadow-sky-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400 disabled:shadow-none dark:shadow-sky-500/10 dark:hover:shadow-sky-500/20 dark:focus-visible:ring-offset-gray-900 dark:disabled:from-gray-700 dark:disabled:to-gray-700"
        >
          {validCount > 0
            ? `Check ${validCount} Domain${validCount === 1 ? '' : 's'}`
            : 'Check Domains'}
        </button>
      </div>
    </form>
  );
}
